/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { format, isAfter, parse } from "date-fns";
import { API_REGION_OVERRIDE } from "../config/constants";
import {
  DEFAULT_TIMEZONE,
  OrderProcedurePriority,
  PERMISSIONS,
  ReportPriorityIconEnum,
  StorageClass,
  yyyyMMdd,
} from "../constant";
import { Issuer } from "../types/issuer";
import { IPages, IPermission } from "../types/permission";
import { CSSProperties } from "react";
import { DateRange } from "../types/components/datetime";
import { formatDateDicom } from "./format";
import { DateTime } from "luxon";
import {
  AdvaPACSViewer,
  formatValidDate,
  GroupedSite,
  HighIconSVG,
  IResourceLock,
  ISite,
  isValid,
  RoutineIconSVG,
  StatIcon,
  TableSortKey,
} from "..";
import axios from "axios";
import { IAssignUserResponse } from "../types/users";
import parsePhoneNumber, { AsYouType } from "libphonenumber-js";

export const formatDate = (date?: Date | string, fmt?: string): string => {
  if (!date) return "";

  if (date === "Invalid Value") return date;
  if (typeof date === "string") {
    return formatDateDicom(fmt, date as string);
  }
  return formatValidDate(date as Date, fmt || "yyyyMMdd");
};

export const formatTime = (
  date: Date,
  showAMPM = true,
  isNextDay = false
): string => {
  let hoursOfDate = date.getHours();
  const minutesOfDate = date.getMinutes();
  const timeFormat = hoursOfDate >= 12 ? "PM" : "AM";
  hoursOfDate %= 12;
  hoursOfDate = hoursOfDate || 12; // the hour '0' should be '12'
  const minutes = minutesOfDate < 10 ? `0${minutesOfDate}` : minutesOfDate;
  const hours = hoursOfDate < 10 ? `0${hoursOfDate}` : hoursOfDate;
  const resultTimeFormat = isNextDay
    ? `${hours}:${minutes}ND`
    : `${hours}:${minutes}`;
  return showAMPM ? `${resultTimeFormat} ${timeFormat}` : `${resultTimeFormat}`;
};

export const formatDateRange = (range?: DateRange, fmt?: string): string => {
  if (!range) return "";
  if (!range.startDate && !range.endDate) return "";
  return `${formatDate(range.startDate as Date, fmt)}-${formatDate(
    range.endDate as Date,
    fmt
  )}`;
};

export const formatAudioDurationDisplay = (timeInSeconds = 0): string => {
  const minutes = Math.floor(timeInSeconds / 60);
  const seconds = Math.round(timeInSeconds % 60);
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
};

export const formatMobileNumber = (mobilePhone: string): string => {
  let mobileNumber = "";
  if (mobilePhone) {
    const prefixValue = parsePhoneNumber(mobilePhone);
    mobileNumber = prefixValue
      ? new AsYouType(prefixValue?.country).input(prefixValue?.number)
      : "";
  }
  return mobileNumber;
};

export const parseDateFromString = (
  dirtyDate?: string,
  fmt?: string,
  isTimeValue?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Date | any | undefined => {
  const isValidDate = isValid(
    parse(dirtyDate, fmt ? fmt : yyyyMMdd, new Date())
  );
  if (isTimeValue || (dirtyDate && isValidDate)) {
    const date = fmt
      ? DateTime.fromFormat(dirtyDate, fmt).toJSDate()
      : DateTime.fromISO(dirtyDate).toJSDate();
    return isValid(date) ? date : undefined;
  }
  if (!isValidDate && dirtyDate) return "Invalid Value";
  return undefined;
};

export interface ILocalisation {
  dateFormat: string;
  dateTimeFormat: string;
  locale: string;
  timeFormat: string;
}

export interface Account {
  id: string;
  email: string;
  locale: string;
  name: string;
  permissions: Array<string>;
  username: string;
  owner: boolean;
  enforceTwoFactor: boolean;
  localisation: ILocalisation;
  forcePasswordChange: boolean;
  trialMode: boolean;
  sessionId: string;
  customerId: string;
  readOnly: boolean;
  defaultIssuerOfPatientId?: Issuer | null;
}

export const hasPermission = (
  permissions: string[] = [],
  can?: string[]
): boolean => {
  return !can || !!can.some((item) => permissions.includes(item));
};

export const isOwner = (account: Account | null): boolean | undefined => {
  return account?.owner;
};

export const filterIntegrationByFeatureStatus = (
  eUnityEnabled: boolean,
  medDreamEnabled: boolean,
  weasisEnabled: boolean,
  permissions: IPermission[] = []
) => {
  return permissions?.filter((perm) => {
    return !(
      (perm.code === PERMISSIONS.ACCESS_WEASIS && !weasisEnabled) ||
      (perm.code === PERMISSIONS.ACCESS_MEDDREAM && !medDreamEnabled) ||
      (perm.code === PERMISSIONS.ACCESS_EUNITY && !eUnityEnabled)
    );
  });
};

export const convertToPermissionOptions = (permissions: Array<IPermission>) => {
  const keys = Object.keys(permissions);
  const values = Object.values(permissions);
  const options: Array<IPages> = [];
  if (keys.length > 0 && values.length > 0) {
    // eslint-disable-next-line no-plusplus
    for (let index = 0; index < keys.length; index++) {
      const key = keys[index];
      const arrSub = values[index] as Array<IPermission>;
      const parentGroup: IPages = {
        label: key,
        value: key,
        key,
        checked: false,
        type: "group",
        expand: false,
        code: "",
        readOnly: false,
      };
      parentGroup.subs =
        arrSub.length > 0
          ? arrSub.map((sub) => {
              return {
                value: sub.id,
                label: sub.title,
                description: sub.description,
                code: sub.code,
                key: sub.groupKey,
                checked: false,
                expand: false,
                parent: parentGroup,
                type: "item",
                readOnly: false,
              };
            })
          : [];
      options.push(parentGroup);
    }
  }
  return options?.sort((a, b) => {
    if (a.label && b.label && a.label < b.label) {
      return -1;
    }
    if (a.label && b.label && a.label > b.label) {
      return 1;
    }
    return 0;
  });
};

export const getStyleTable = (width = 170): CSSProperties => {
  return {
    boxSizing: "border-box",
    flex: `${width} 0 auto`,
    minWidth: 0,
    width: width,
    position: "relative",
  };
};

export const setHours = (date: Date, hours = 0, min = 0, sec = 0, ms = 0) => {
  if (!date) return;
  return date.setHours(hours, min, sec, ms);
};

export const setEndDate = (date: Date) => {
  if (!date) return;
  // @ts-ignore
  return new Date(setHours(date, 23, 59, 59, 0));
};

// a function to retry loading a chunk to avoid chunk load error for out of date code
export const routeLoader = <T>(
  factory: () => Promise<T>,
  maxRetry = 3
): Promise<T> => {
  return new Promise((resolve, reject) => {
    // check if the window has already been refreshed
    const hasRefreshed = JSON.parse(
      window.sessionStorage.getItem("retry-lazy-refreshed") || "false"
    );
    factory()
      .then((component) => {
        window.sessionStorage.setItem("retry-lazy-refreshed", "false"); // success so reset the refresh
        resolve(component);
      })
      .catch((error) => {
        if (maxRetry === 0) {
          if (!hasRefreshed) {
            // not been refreshed yet
            window.sessionStorage.setItem("retry-lazy-refreshed", "true"); // we are now going to refresh
            return {}; // refresh the page
          }
          reject(error);
          // this is probably a white page error. reload the main page
          // alt - add better messaging or load another page
          // window.location.reload();
          return;
        }

        // recursively try again reducing the retry count
        routeLoader(factory, --maxRetry).then(resolve, reject);
      });
  });
};

export const range = (start: number, end: number, step = 1): Array<number> => {
  const len = Math.floor((end - start) / step) + 1;
  return Array(len)
    .fill(0)
    .map((_, idx) => start + idx * step);
};

export const hasAllOfflineStorageClass = (
  items: {
    storageClasses: StorageClass[];
  }[]
) => {
  if (items.length === 0) return false;
  return items
    .map((s) => s.storageClasses)
    .flat()
    .every((s) => s === StorageClass.OFFLINE);
};

export const hasSomeOfflineStorageClass = (
  items: {
    storageClasses: StorageClass[];
  }[]
) => {
  if (items.length === 0) return false;
  const storagesClasses = items.map((s) => s.storageClasses).flat();
  const offlineSC = storagesClasses.filter((s) => s === StorageClass.OFFLINE);
  return offlineSC.length > 0 && offlineSC.length < storagesClasses.length;
};

export const blobToBase64 = (blob: Blob) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  return new Promise((resolve, _) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(blob);
  });
};

export interface StyleAttributes {
  [key: string]: string | number | boolean;
}

export const getDragItemStyle = (
  xDragging: number,
  currentWidthDrag = 170,
  isDark = false
): CSSProperties => {
  return {
    position: "absolute",
    zIndex: 999999,
    transform: `translateX(${xDragging}px)`,
    transition: `width transform 0.2s ease-in-out`,
    background: isDark ? "#131823" : "#d1d1d1",
    opacity: 0.8,
    width: currentWidthDrag,
  };
};

export const getKeyFromCol = (col) => {
  // eslint-disable-next-line @typescript-eslint/dot-notation
  return col.id || col["accessor"] || col["Header"];
};

export const getLockedString = (
  locks: IResourceLock[],
  rowId: string,
  dateTimeFormat: string
): string => {
  const locked = locks.map((x) => x.resourceId).includes(rowId);

  const lockRecord: IResourceLock =
    locks.find((x) => x.resourceId === rowId) || null;
  if (locked && lockRecord?.message) return lockRecord.message;
  const lockedDate =
    lockRecord?.lockedSince &&
    isValid(new Date(lockRecord.lockedSince.slice(0, 27)))
      ? new Date(lockRecord.lockedSince.slice(0, 27))
      : null;
  return locked && lockRecord !== null
    ? `Locked by ${lockRecord?.lockedBy} since ${
        lockedDate ? format(lockedDate, dateTimeFormat) : ""
      }`
    : "";
};

export const parsePatientNameDicom = (patientName: string) => {
  if (!patientName) return null;
  const patientNameArr = patientName.split("^");
  const lastName = patientNameArr.length >= 1 ? patientNameArr[0] : "";
  const firstName = patientNameArr.length >= 2 ? patientNameArr[1] : "";
  const middleName = patientNameArr.length >= 3 ? patientNameArr[2] : "";
  const prefix = patientNameArr.length >= 4 ? patientNameArr[3] : "";
  const suffix = patientNameArr.length >= 5 ? patientNameArr[4] : "";
  return {
    lastName,
    firstName,
    middleName,
    prefix,
    suffix,
  };
};

export const retryIfFailed = async (url: string, times = 4) => {
  let i = 0;
  let rs;
  do {
    try {
      rs = await axios.get(url);
    } catch (ex) {
    } finally {
      i++;
    }
  } while (i < times && rs?.data === undefined);
  return rs;
};

export const capitalizeFirstLetter = (str?: string): string => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const getNextHigherIndex = (
  id: string,
  data: { id: string; index: number }[]
): number => {
  try {
    const index = data.findIndex((item) => item.id === id);
    return data[index + 1].index;
  } catch (e) {
    return -1;
  }
};

export const getNextLowerIndex = (
  id: string,
  data: { id: string; index: number }[]
): number => {
  try {
    const index = data.findIndex((item) => item.id === id);
    return data[index - 1].index;
  } catch (e) {
    return -1;
  }
};
// specific case when converting sort keys for API can handle easy.
const convertSortKey = (key: string) => {
  let requestKey = key;
  switch (key) {
    case "reportPriority":
      requestKey = "reportPriority.index";
      break;
    default:
      break;
  }
  return requestKey;
};

const convertSortKeyWithoutOrdersRoot = (
  key: string,
  rootCriteria = "orders"
) => {
  let requestKey = key;
  switch (key) {
    case "pid":
      requestKey = `${rootCriteria}.patient.pids.pid`;
      break;
    case "actStatus": // Fix issue : AX-6187: Error 500 when sort column "Action Status"
      // Return the exact column field for sorting in BE side
      return "actionStatus";
    default:
      break;
  }
  return requestKey;
};

export const getSortKeysRequest = (keys: TableSortKey[]) => {
  return keys.map((x) => {
    return {
      ...x,
      id: convertSortKey(x.id),
    };
  });
};

export const getSortKeyWithoutOrdersRoot = (
  keys: TableSortKey[],
  rootCriteria = "orders"
) => {
  return keys.map((x) => {
    return {
      ...x,
      id: convertSortKeyWithoutOrdersRoot(x.id, rootCriteria),
    };
  });
};

export const formatAssignedUser = (user?: IAssignUserResponse) => {
  return user ? `${user.firstName || ""} ${user.lastName || ""}` : "";
};

export const getGroupSiteOptions = (sites: ISite[]): Array<GroupedSite> => {
  const groups: GroupedSite[] = [];
  sites.forEach((i) => {
    const groupIndex = groups.findIndex((grp) => grp.id === i.organisation?.id);
    if (groupIndex >= 0) {
      const groupContent = { ...groups[groupIndex] };
      groupContent.options.push({ value: i.id, label: i.name });
      groups[groupIndex] = groupContent;
    } else if (i.organisation) {
      const grp = {
        id: i.organisation?.id,
        label: i.organisation?.name,
        options: [{ value: i.id, label: i.name }],
      };
      groups.push(grp);
    }
  });
  return groups;
};

export const graceFullyPhoneFormat = (phone: string) => {
  // Remove any characters except numerics
  return "+" + phone.replace(/[^0-9]/g, "");
};

export const prodModeLog = (data: string) => {
  const enabledProdLog = localStorage.getItem("enabled_log");
  if (enabledProdLog) {
    // eslint-disable-next-line no-console
    console.log({ data });
  }
};

export const isDay = () => {
  const hour = new Date().getHours(); // Get current hour (0-23)
  return hour >= 6 && hour < 18;
};

export const toBase64 = (file: File) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const getBlobUrlForPDFPreview = async (
  hasPrams?: boolean, // hasParams
  path?: string, // API Path
  baseURL?: string // AXIOS BASEURL instance
) => {
  const params: Record<string, string> = {
    origin: window.origin,
    "x-tz": DEFAULT_TIMEZONE.value,
  };
  const urlParams = new URLSearchParams(params);
  const url = `${baseURL}${path}`;
  const finalUrl = hasPrams ? `${url}?${urlParams.toString()}` : url;

  const response = await axios.get(finalUrl, { responseType: "blob" });
  const blob = new Blob([response.data], { type: "application/pdf" });
  return window.URL.createObjectURL(blob);
};

export const isSafari = () => {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
};

export const localStorageHasValue = (key: string) => {
  return localStorage.getItem(key) && localStorage.getItem(key) !== "";
};

export const getOrderProcedurePriorityIcon = (priority: string) => {
  switch (priority) {
    case OrderProcedurePriority.STAT:
      return StatIcon;
    case OrderProcedurePriority.ASAP:
    case OrderProcedurePriority.URGENT:
      return HighIconSVG;
    default:
      return RoutineIconSVG;
  }
};

export const reCaptchaDelay = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const delayTime = (ms: number): Promise<void> => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const generateIdSequence = (
  prefix: string,
  number: string,
  minLength: string,
  maxLengthAllowed = 20 // if this minLength too big, padStart can make more memory. it should limit it
): string => {
  if (!number || !minLength) return "";
  // Ensure the number is a string with leading zeros
  let lengthNum = parseInt(minLength, 10);
  if (maxLengthAllowed < lengthNum) {
    lengthNum = maxLengthAllowed;
  }
  const padded = String(number).padStart(lengthNum, "0");
  return (prefix || "") + padded;
};

export const generateCurrentTime = (): string => {
  return new Date().toISOString().slice(-24).replace(/\D/g, "").slice(0, 16);
};

export const reverseString = (str: string): string => {
  return str.split("").reverse().join("");
};

export const copyLinkWithOrigin = (pathName: string) => {
  navigator.clipboard.writeText(`${window.location.origin}${pathName}`);
};

export const configurationLinkDetails = (
  routerName: string,
  routerPathId: string
) => {
  return `/Configuration/${routerName}/${routerPathId}`;
};

export const adminLinkDetails = (routerName: string, routerPathId: string) => {
  return `/Admin/${routerName}/${routerPathId}`;
};

export const worklistAdminLinkDetails = (
  routerName: string,
  routerPathId: string
) => {
  return `/Worklist/Admin/${routerName}/${routerPathId}`;
};

export const trimObject = <T>(value: T): T => {
  if (typeof value === "string") {
    return value.trim() as T;
  }

  if (value instanceof Date) {
    return value;
  }

  // FormData, Blob, and File are objects but their entries are not enumerable
  // via Object.keys — trimming them would produce an empty plain object and
  // break multipart/form-data requests (e.g. document upload → 400).
  if (
    value instanceof FormData ||
    value instanceof Blob ||
    value instanceof File
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((v) => trimObject(v)) as T;
  }

  if (value !== null && typeof value === "object") {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const result: any = {};
    Object.keys(value as any).forEach((key) => {
      result[key] = trimObject((value as any)[key]);
    });
    return result;
  }

  return value;
};

/**
 * Convert enum KEY -> VALUE
 * EX: "OBSTETRICS" -> "B"
 */
export const enumKeyToValue = <T extends Record<string, string>>(
  enumObj: T,
  key?: any | null
): T[keyof T] | undefined => {
  if (!key) return undefined;
  return enumObj[key as keyof T];
};

/**
 * Convert enum VALUE -> KEY
 * EX: "B" -> "OBSTETRICS"
 */
export const enumValueToKey = <T extends Record<string, string>>(
  enumObj: T,
  value?: any | null
): keyof T | undefined => {
  if (!value) return undefined;

  return (Object.keys(enumObj) as Array<keyof T>).find(
    (k) => enumObj[k] === value
  );
};

/**
 * Convert list KEY -> VALUE
 */
export const enumKeysToValues = <T extends Record<string, string>>(
  enumObj: T,
  keys?: Array<any | null>
): Array<T[keyof T]> => {
  if (!keys) return [];
  return keys.map((k) => enumKeyToValue(enumObj, k)).filter(Boolean) as Array<
    T[keyof T]
  >;
};

/**
 * Convert list VALUE -> KEY
 */
export const enumValuesToKeys = <T extends Record<string, string>>(
  enumObj: T,
  values?: Array<any | null>
): Array<keyof T> => {
  if (!values) return [];
  return values.map((v) => enumValueToKey(enumObj, v)).filter(Boolean) as Array<
    keyof T
  >;
};

export const normalizeDateRange = (
  startDate: Date | null,
  endDate: Date | null
) => {
  if (startDate && endDate && isAfter(startDate, endDate)) {
    return {
      startDate,
      endDate: startDate,
    };
  }

  return {
    startDate,
    endDate,
  };
};

export const removeFHIRCastTopic = () => {
  // [REMOVE SESSION STORAGE] fhircast topic ID
  sessionStorage.removeItem("fhircast-tab-id");
  sessionStorage.removeItem("fhircast-topic-id");
  sessionStorage.removeItem("fhircast-is-reloading");
};

export const generateAdvaViewPath = (
  viewer: AdvaPACSViewer,
  enabledLaunchViewerWithDiagnosticVersion: boolean
) => {
  if (viewer === AdvaPACSViewer.DISABLED) return;
  if (
    enabledLaunchViewerWithDiagnosticVersion &&
    viewer === AdvaPACSViewer.DIAGNOSTIC
  ) {
    return "DiagnosticViewer/diagnostic";
  }

  return "Viewer/standard";
};

// report priority icon is a JSON string ({ code, color }); invalid JSON must not break rendering.
export const parseReportPriorityIcon = (
  icon: unknown
): { code: ReportPriorityIconEnum; color: string } | undefined => {
  if (!icon || typeof icon !== "string") return undefined;
  try {
    return JSON.parse(icon);
  } catch {
    return undefined;
  }
};

export const getRegion = (): string => {
  let region: string | null = API_REGION_OVERRIDE;
  if (!region) {
    // Region is resolved when the application first loads and cached in local storage.
    // There is no need to resolve it again.
    region = localStorage.getItem(`${window.location.host}.region`);
  }

  if (!region) {
    // eslint-disable-next-line no-console
    console.error("Cannot find the region in the cache");
    return "";
  }
  return region;
};
